import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useWorkshop } from "../../contexts/WorkshopContext";
import ApiService from "../../services/api";

interface DashboardStats {
  totalInspections: number;
  openInspections: number;
  closedInspections: number;
  totalVehicles: number;
  passedItems: number;
  failedItems: number;
  naItems: number;
  recentInspections: any[];
  inspectionsByType: Record<string, number>;
  inspectionsByDay: Array<{ date: string; count: number }>;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { selectedWorkshop } = useWorkshop();
  const [stats, setStats] = useState<DashboardStats>({
    totalInspections: 0,
    openInspections: 0,
    closedInspections: 0,
    totalVehicles: 0,
    passedItems: 0,
    failedItems: 0,
    naItems: 0,
    recentInspections: [],
    inspectionsByType: {},
    inspectionsByDay: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log(
      "🔄 Workshop changed in Dashboard, loading data for:",
      selectedWorkshop?.name
    );
    if (selectedWorkshop) {
      loadDashboardData();
    }
  }, [selectedWorkshop]);

  const loadDashboardData = async () => {
    if (!selectedWorkshop) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Cargar datos del dashboard para el workshop seleccionado
      const dashboardData = await ApiService.get<any>(
        `/inspections/stats/dashboard?workshop_id=${selectedWorkshop.id}`
      );

      setStats({
        totalInspections: dashboardData.totalInspections || 0,
        openInspections: dashboardData.openInspections || 0,
        closedInspections: dashboardData.closedInspections || 0,
        totalVehicles: dashboardData.totalVehicles || 0,
        passedItems: dashboardData.passedItems || 0,
        failedItems: dashboardData.failedItems || 0,
        naItems: dashboardData.naItems || 0,
        recentInspections: dashboardData.recentInspections || [],
        inspectionsByType: dashboardData.inspectionsByType || {},
        inspectionsByDay: dashboardData.inspectionsByDay || [],
      });
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-b-2 ${
            isDarkMode ? "border-orange-400" : "border-orange-600"
          }`}
        ></div>
      </div>
    );
  }

  if (!selectedWorkshop) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <svg
            className={`mx-auto h-12 w-12 ${
              isDarkMode ? "text-gray-600" : "text-gray-400"
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <h3
            className={`mt-2 text-sm font-medium ${
              isDarkMode ? "text-gray-300" : "text-gray-900"
            }`}
          >
            No workshop selected
          </h3>
          <p
            className={`mt-1 text-sm ${
              isDarkMode ? "text-gray-500" : "text-gray-500"
            }`}
          >
            Please select a workshop to view dashboard data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Inspections Card */}
        <div
          className={`rounded-lg shadow p-6 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Total Inspections
              </p>
              <p
                className={`text-3xl font-bold mt-2 ${
                  isDarkMode ? "text-purple-400" : "text-purple-600"
                }`}
              >
                {stats.totalInspections}
              </p>
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
              >
                {stats.openInspections} open • {stats.closedInspections} closed
              </p>
            </div>
            <div
              className={`p-3 rounded-full ${
                isDarkMode ? "bg-purple-900/30" : "bg-purple-600"
              }`}
            >
              <svg
                className={
                  isDarkMode ? "w-8 h-8 text-purple-400" : "w-8 h-8 text-white"
                }
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Total Vehicles Card */}
        <div
          className={`rounded-lg shadow p-6 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Total Vehicles
              </p>
              <p
                className={`text-3xl font-bold mt-2 ${
                  isDarkMode ? "text-blue-400" : "text-blue-600"
                }`}
              >
                {stats.totalVehicles}
              </p>
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
              >
                Unique vehicles inspected
              </p>
            </div>
            <div
              className={`p-3 rounded-full ${
                isDarkMode ? "bg-blue-900/30" : "bg-blue-600"
              }`}
            >
              <svg
                className={
                  isDarkMode ? "w-8 h-8 text-blue-400" : "w-8 h-8 text-white"
                }
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Passed Items Card */}
        <div
          className={`rounded-lg shadow p-6 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Passed Items
              </p>
              <p
                className={`text-3xl font-bold mt-2 ${
                  isDarkMode ? "text-green-400" : "text-green-600"
                }`}
              >
                {stats.passedItems}
              </p>
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
              >
                {stats.failedItems} failed • {stats.naItems} N/A
              </p>
            </div>
            <div
              className={`p-3 rounded-full ${
                isDarkMode ? "bg-green-900/30" : "bg-green-600"
              }`}
            >
              <svg
                className={
                  isDarkMode ? "w-8 h-8 text-green-400" : "w-8 h-8 text-white"
                }
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Open Inspections Card */}
        <div
          className={`rounded-lg shadow p-6 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Open Inspections
              </p>
              <p
                className={`text-3xl font-bold mt-2 ${
                  isDarkMode ? "text-orange-400" : "text-orange-600"
                }`}
              >
                {stats.openInspections}
              </p>
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
              >
                Pending completion
              </p>
            </div>
            <div
              className={`p-3 rounded-full ${
                isDarkMode ? "bg-orange-900/30" : "bg-orange-600"
              }`}
            >
              <svg
                className={
                  isDarkMode ? "w-8 h-8 text-orange-400" : "w-8 h-8 text-white"
                }
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Items Status Distribution */}
      <div
        className={`rounded-lg shadow p-6 mb-6 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <h2
          className={`text-lg font-semibold mb-4 ${
            isDarkMode ? "text-gray-100" : "text-gray-900"
          }`}
        >
          Items Status Distribution
        </h2>
        <div className="flex items-center justify-center h-48">
          {stats.passedItems + stats.failedItems + stats.naItems > 0 ? (
            <div className="w-full max-w-sm">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Passed
                    </span>
                  </div>
                  <span
                    className={`text-sm font-bold ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    {stats.passedItems}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Failed
                    </span>
                  </div>
                  <span
                    className={`text-sm font-bold ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    {stats.failedItems}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-gray-400 rounded mr-2"></div>
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      N/A
                    </span>
                  </div>
                  <span
                    className={`text-sm font-bold ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    {stats.naItems}
                  </span>
                </div>
                <div
                  className={`pt-3 ${
                    isDarkMode
                      ? "border-t border-gray-700"
                      : "border-t border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between font-semibold">
                    <span
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Total
                    </span>
                    <span
                      className={`text-sm ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      {stats.passedItems + stats.failedItems + stats.naItems}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              No data available
            </p>
          )}
        </div>
      </div>

      {/* Inspections Trend (Last 7 Days) */}
      <div
        className={`rounded-lg shadow p-6 mb-6 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <h2
          className={`text-lg font-semibold mb-4 ${
            isDarkMode ? "text-gray-100" : "text-gray-900"
          }`}
        >
          Inspections Trend (Last 7 Days)
        </h2>
        <div className="flex items-end justify-between h-48 space-x-2">
          {stats.inspectionsByDay.map((day, index) => {
            const maxCount = Math.max(
              ...stats.inspectionsByDay.map((d) => d.count),
              1
            );
            const height = (day.count / maxCount) * 100;
            const date = new Date(day.date);
            const dayLabel = date.toLocaleDateString("en-US", {
              weekday: "short",
            });

            return (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div className="w-full flex items-end justify-center flex-1">
                  <div
                    className={`w-full rounded-t transition-colors relative group ${
                      isDarkMode
                        ? "bg-orange-600 hover:bg-orange-700"
                        : "bg-orange-600 hover:bg-orange-700"
                    }`}
                    style={{
                      height: `${height}%`,
                      minHeight: day.count > 0 ? "20px" : "0",
                    }}
                  >
                    <span
                      className={`absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {day.count}
                    </span>
                  </div>
                </div>
                <span
                  className={`text-xs mt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {dayLabel}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Inspections */}
      <div
        className={`rounded-lg shadow ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div
          className={`px-6 py-4 ${
            isDarkMode ? "border-b border-gray-700" : "border-b border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-gray-100" : "text-gray-900"
            }`}
          >
            Recent Inspections
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={isDarkMode ? "bg-gray-900" : "bg-gray-50"}>
              <tr>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  ID
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Vehicle
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Status
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Created
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              className={`divide-y ${
                isDarkMode
                  ? "bg-gray-800 divide-gray-700"
                  : "bg-white divide-gray-200"
              }`}
            >
              {stats.recentInspections.map((inspection) => (
                <tr
                  key={inspection.id}
                  className={
                    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                  }
                >
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    #{inspection.id}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Vehicle #{inspection.vehicle_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        inspection.status === "open"
                          ? isDarkMode
                            ? "bg-green-900/30 text-green-400"
                            : "bg-green-100 text-green-800"
                          : isDarkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {inspection.status}
                    </span>
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {new Date(inspection.created_at).toLocaleDateString()}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${
                      isDarkMode ? "text-orange-400" : "text-orange-600"
                    }`}
                  >
                    <button
                      onClick={() =>
                        navigate(`/inspections/${inspection.id}/details`)
                      }
                      className="hover:text-blue-800 font-medium"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
              {stats.recentInspections.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No inspections found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
